import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Waves } from "lucide-react";
import {
  recordMood,
  getTodayMood,
  getRecentMoods,
  buildWavePath,
} from "@/lib/moodWave";

const MOODS = [
  { value: 1, label: "heavy", color: "#5b5f97" },
  { value: 2, label: "low", color: "#6d7fa8" },
  { value: 3, label: "okay", color: "#6fa8a0" },
  { value: 4, label: "light", color: "#8fbc8f" },
  { value: 5, label: "bright", color: "#d4a86a" },
];

const W = 320;
const H = 110;

// One tap a day — a quiet wave over weeks that shows highs and lows
// always keep moving. On-device only, no metrics language.
// by David Hunt
export const MoodWave = ({ dimmed = false }) => {
  const [open, setOpen] = useState(false);
  const [today, setToday] = useState(null);
  const [moods, setMoods] = useState([]);

  useEffect(() => {
    if (open) {
      setToday(getTodayMood());
      setMoods(getRecentMoods(28));
    }
  }, [open]);

  const handleTap = (value) => {
    recordMood(value);
    setToday(value);
    setMoods(getRecentMoods(28));
  };

  const { path, points } = buildWavePath(moods, W, H);
  const recorded = points.length;

  return (
    <>
      <button
        onClick={() => {
          toast.dismiss();
          setOpen(true);
        }}
        className={`fixed w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/85 transition-opacity duration-[1500ms] ease-in-out z-10 ${
          dimmed ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{
          bottom: "calc(env(safe-area-inset-bottom) + 1rem)",
          right: "calc(env(safe-area-inset-right) + 1rem)",
        }}
        data-testid="mood-wave-btn"
        aria-label="A one-tap mood check"
        title="your wave"
      >
        <Waves size={16} strokeWidth={1.5} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mood-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
            data-testid="mood-wave-overlay"
          >
            <div
              className="fixed inset-0 bg-black/45 backdrop-blur-2xl pointer-events-none"
              aria-hidden="true"
            />

            <button
              onClick={() => setOpen(false)}
              className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
              style={{
                top: "calc(env(safe-area-inset-top) + 1rem)",
                right: "calc(env(safe-area-inset-right) + 1rem)",
              }}
              data-testid="mood-close-btn"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <div className="relative min-h-full flex flex-col items-center justify-center px-6 py-20">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0, ease: "easeInOut", delay: 0.15 }}
                className="w-full max-w-sm text-center"
              >
                <h2 className="font-heading italic text-white/90 text-2xl sm:text-3xl">
                  how is it in there?
                </h2>
                <p className="mt-2 text-xs text-white/40 tracking-wider uppercase">
                  one tap · kept only on this device
                </p>

                <div className="mt-10 flex items-end justify-center gap-4 sm:gap-5">
                  {MOODS.map((m) => (
                    <button
                      key={m.value}
                      onClick={() => handleTap(m.value)}
                      className="flex flex-col items-center gap-2 group"
                      data-testid={`mood-tap-${m.value}`}
                      aria-label={`Feeling ${m.label}`}
                    >
                      <span
                        className="rounded-full transition-all duration-500 border"
                        style={{
                          width: today === m.value ? 40 : 30,
                          height: today === m.value ? 40 : 30,
                          backgroundColor:
                            today === m.value ? m.color : `${m.color}55`,
                          borderColor:
                            today === m.value
                              ? "rgba(255,255,255,0.5)"
                              : "rgba(255,255,255,0.15)",
                        }}
                      />
                      <span className="text-[10px] text-white/40 group-hover:text-white/70 tracking-wide transition-colors duration-500">
                        {m.label}
                      </span>
                    </button>
                  ))}
                </div>

                {today != null && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.0, ease: "easeInOut" }}
                    className="mt-6 text-sm text-white/55"
                    data-testid="mood-noted"
                  >
                    noted, gently.
                  </motion.p>
                )}

                <div className="mt-10">
                  {recorded >= 2 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 1.4, ease: "easeInOut" }}
                    >
                      <svg
                        viewBox={`0 0 ${W} ${H}`}
                        className="w-full"
                        data-testid="mood-wave-svg"
                      >
                        <path
                          d={path}
                          fill="none"
                          stroke="rgba(255,255,255,0.55)"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        {points.map((p, i) => (
                          <circle
                            key={i}
                            cx={p.x}
                            cy={p.y}
                            r="3"
                            fill="rgba(255,255,255,0.7)"
                          />
                        ))}
                      </svg>
                      <p className="mt-4 text-xs text-white/45 leading-relaxed">
                        your last four weeks. the wave keeps moving — highs and
                        lows always pass.
                      </p>
                    </motion.div>
                  ) : (
                    <p
                      className="text-xs text-white/40 leading-relaxed"
                      data-testid="mood-wave-empty"
                    >
                      tap again another day and a quiet wave will appear here —
                      proof that no feeling stays still.
                    </p>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MoodWave;
